// Teacher exercise library: browsing, authoring, assignment.
import { loadAccount } from "../data/account.js";
import {
  bindCurriculumBrowser,
  canonicalExerciseCount,
  curriculumBrowser,
  groupTeacherExercises,
  renderTeacherExerciseHierarchy
} from "../domain/curriculum.js";
import { getAIGenerationTopicOptions } from "../features/exercise-authoring.js";
import { copy, t } from "../i18n.js";
import { readableError } from "../lib/api.js";
import { closeModal, escapeHTML, setBusy, showModal, showToast } from "../lib/dom.js";
import { difficultyDisplay } from "../lib/format.js";
import { render } from "../router.js";
import { state } from "../state.js";
import { supabase } from "../supabase-client.js";
import { appLayout } from "../ui/layout.js";

export function teacherExercisesView() {
  const groupedClasses = groupTeacherExercises(state.teacherExercises);
  const teacherGrades = new Set(state.schoolClasses
    .filter(item => item.teacher_name === state.account.username)
    .map(item => Number(item.grade_level)));
  const catalogContent = curriculumBrowser(state.teacherCatalogExercises, "teacher", teacherGrades);
  const content = groupedClasses.length
    ? groupedClasses.map(classroom => `
      <section class="card teacher-class-library">
        <div class="library-heading">
          <div>
            <span class="eyebrow">${escapeHTML(classroom.school)}</span>
            <h2>${escapeHTML(classroom.name)}</h2>
            <p>${escapeHTML(classroom.academicYear)}</p>
          </div>
          <span class="library-count">${canonicalExerciseCount(classroom)} ${state.language === "it" ? "esercizi" : "exercises"}</span>
        </div>
        <div class="teacher-exercise-hierarchy">${renderTeacherExerciseHierarchy(classroom)}</div>
      </section>`).join("")
    : `<section class="card empty-state">
        <div class="empty-icon">✎</div>
        <h2>${state.language === "it" ? "Nessun esercizio creato" : "No created exercises"}</h2>
        <p>${state.language === "it" ? "Genera il primo esercizio dalla dashboard insegnante." : "Generate the first exercise from the teacher dashboard."}</p>
      </section>`;

  appLayout(
    `<div class="stack teacher-exercises-page">
      <div class="library-actions">
        <button class="secondary" id="open-ai-library">${state.language === "it" ? "Crea o genera nuovi esercizi" : "Create or generate new exercises"}</button>
      </div>
      <section class="card workspace-catalog-card">
        <div class="library-heading">
          <div>
            <span class="eyebrow">BrainMath</span>
            <h2>${state.language === "it" ? "Esercizi disponibili da assegnare" : "Available exercises to assign"}</h2>
            <p>${state.language === "it" ? "Materiale organizzato per anno scolastico, assegnabile senza consumare crediti AI." : "Grade-based material that can be assigned without using AI credits."}</p>
          </div>
        </div>
        <div class="topic-groups">${catalogContent}</div>
      </section>
      <div class="workspace-section-title"><div><span class="eyebrow">${state.language === "it" ? "Attività in corso" : "Active work"}</span><h2>${state.language === "it" ? "Esercizi assegnati alle mie classi" : "Exercises assigned to my classes"}</h2></div><span>${groupedClasses.length} ${state.language === "it" ? "classi" : "classes"}</span></div>
      ${content}
    </div>`,
    state.language === "it" ? "Esercizi per classe" : "Exercises by class",
    state.language === "it"
      ? "Scuola e classe, poi area matematica, argomento, difficoltà e attività."
      : "School and class, followed by maths area, topic, difficulty and activity."
  );
  document.querySelector("#open-ai-library").addEventListener("click", () => {
    state.view = "ai";
    render();
  });
  bindTeacherExerciseLinks();
  bindCurriculumBrowser("teacher", teacherExercisesView);
  document.querySelectorAll("[data-assign-catalog]").forEach(button => {
    button.addEventListener("click", () => assignCatalogExerciseModal(button.dataset.assignCatalog));
  });
}

function assignCatalogExerciseModal(exerciseID) {
  const exercise = state.teacherCatalogExercises.find(item => item.exercise_id === exerciseID);
  if (!exercise) return;
  const range = state.catalogGradeRanges[exerciseID];
  const compatibleClasses = state.schoolClasses.filter(item =>
    item.teacher_name === state.account.username &&
    (!range || (Number(item.grade_level) >= range.min_grade && Number(item.grade_level) <= range.max_grade))
  );
  showModal(`
    <div class="modal-header"><h2>${state.language === "it" ? "Assegna esercizio" : "Assign exercise"}</h2><button class="close-button" data-close-modal>×</button></div>
    <p><strong>${escapeHTML(exercise.title)}</strong><br>${escapeHTML(exercise.topic)}</p>
    <form id="assign-catalog-form" class="form-grid">
      <label>${state.language === "it" ? "Classe compatibile" : "Compatible class"}<select name="classID" required>
        <option value="">${state.language === "it" ? "Seleziona classe" : "Select class"}</option>
        ${compatibleClasses.map(classroom => `<option value="${classroom.class_id}">${escapeHTML(classroom.school_name)} · ${escapeHTML(classroom.class_name)} · ${escapeHTML(classroom.academic_year_name)}</option>`).join("")}
      </select></label>
      <button class="primary full" type="submit">${state.language === "it" ? "Assegna alla classe" : "Assign to class"}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#assign-catalog-form");
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const classID = new FormData(form).get("classID");
    const { error } = await supabase.rpc("assign_catalog_exercise_to_class", {
      selected_exercise: exerciseID,
      selected_class: classID
    });
    if (error) {
      form.querySelector(".form-message").textContent = error.message;
      return;
    }
    closeModal();
    await loadAccount();
    teacherExercisesView();
    showToast(state.language === "it" ? "Esercizio assegnato alla classe." : "Exercise assigned to the class.");
  });
}

export function bindTeacherExerciseLinks() {
  document.querySelectorAll("[data-open-teacher-exercise]").forEach(button => {
    button.addEventListener("click", () => showTeacherExerciseDetail(button.dataset.openTeacherExercise));
  });
}

async function showTeacherExerciseDetail(exerciseID) {
  showModal(`<div class="empty-state"><div class="empty-icon">…</div><p>${t("loading")}</p></div>`);
  const { data, error } = await supabase.rpc("get_teacher_exercise_detail", {
    selected_exercise: exerciseID,
    selected_language: state.language
  });
  if (error || !data?.[0]) {
    closeModal();
    showToast(error?.message || (state.language === "it" ? "Esercizio non disponibile." : "Exercise unavailable."));
    return;
  }
  const exercise = data[0];
  const questions = (exercise.questions || []).map((question, index) => `
    <details class="question-detail" ${index === 0 ? "open" : ""}>
      <summary><strong>${index + 1}. ${escapeHTML(question.prompt)}</strong></summary>
      <div class="question-detail-body">
        <div class="answer-preview">
          ${question.answers.map(answer => `<span class="${answer === question.correct_answer ? "correct-answer" : ""}">${escapeHTML(answer)}</span>`).join("")}
        </div>
        <p><strong>${state.language === "it" ? "Risposta corretta" : "Correct answer"}:</strong> ${escapeHTML(question.correct_answer)}</p>
        <p><strong>${state.language === "it" ? "Suggerimento" : "Hint"}:</strong> ${escapeHTML(question.hint)}</p>
        <p><strong>${state.language === "it" ? "Spiegazione" : "Explanation"}:</strong> ${escapeHTML(question.explanation)}</p>
      </div>
    </details>`).join("");
  closeModal();
  showModal(`
    <div class="modal-header">
      <div>
        <span class="eyebrow">${escapeHTML(exercise.school_name || "")} · ${escapeHTML(exercise.class_name || "")}</span>
        <h2>${escapeHTML(exercise.title)}</h2>
      </div>
      <button class="close-button" data-close-modal>×</button>
    </div>
    <p style="color:var(--muted)">${escapeHTML(exercise.topic)} · ${difficultyDisplay(exercise.difficulty_level)} · ${escapeHTML(exercise.academic_year_name || "")}</p>
    <div class="stat-grid" style="margin:16px 0">
      <div class="stat"><strong>${exercise.questions.length}</strong><span>${t("questions")}</span></div>
      <div class="stat"><strong>${exercise.assigned_students}</strong><span>${state.language === "it" ? "assegnati" : "assigned"}</span></div>
      <div class="stat"><strong>${exercise.completed_students}</strong><span>${state.language === "it" ? "completati" : "completed"}</span></div>
    </div>
    <button class="secondary full" id="append-question" style="margin-bottom:16px">${state.language === "it" ? "Aggiungi nuove domande" : "Add new questions"}</button>
    ${state.roles.includes("teacher") ? `<button class="secondary full" id="copy-exercise" style="margin-bottom:16px">${state.language === "it" ? "Copia in un'altra classe" : "Copy to another class"}</button>` : ""}
    <div class="question-details">${questions}</div>`);
  document.querySelector("#append-question").addEventListener("click", () => appendQuestionModal(exerciseID));
  document.querySelector("#copy-exercise")?.addEventListener("click", () => copyExerciseModal(exerciseID));
}

function copyExerciseModal(exerciseID) {
  const teacherClasses = state.schoolClasses.filter(item => item.teacher_name === state.account.username);
  showModal(`
    <div class="modal-header"><h2>${state.language === "it" ? "Copia esercizio" : "Copy exercise"}</h2><button class="close-button" data-close-modal>×</button></div>
    <form id="copy-exercise-form" class="form-grid">
      <label>${state.language === "it" ? "Classe di destinazione" : "Destination class"}<select name="classID" required>
        <option value="">${state.language === "it" ? "Seleziona classe" : "Select class"}</option>
        ${teacherClasses.map(classroom => `<option value="${classroom.class_id}">${escapeHTML(classroom.school_name)} · ${escapeHTML(classroom.class_name)} · ${escapeHTML(classroom.academic_year_name)}</option>`).join("")}
      </select></label>
      <button class="primary full" type="submit">${state.language === "it" ? "Copia" : "Copy"}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#copy-exercise-form");
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const selectedClassID = new FormData(form).get("classID");
    const { error } = await supabase.rpc("clone_teacher_exercise_to_class", {
      selected_exercise: exerciseID,
      selected_class: selectedClassID
    });
    if (error) {
      form.querySelector(".form-message").textContent = error.message || "Unable to copy the exercise.";
      return;
    }
    closeModal();
    await loadAccount();
    showToast(state.language === "it" ? "Esercizio copiato nella classe selezionata." : "Exercise copied to the selected class.");
  });
}

function localizedQuestionFields(prefix = "") {
  const labels = state.language === "it"
    ? { prompt: "Domanda", correct: "Risposta corretta", wrong: "Risposta errata", hint: "Suggerimento", explanation: "Spiegazione" }
    : { prompt: "Question", correct: "Correct answer", wrong: "Wrong answer", hint: "Hint", explanation: "Explanation" };
  return `
    <fieldset class="translation-panel">
      <legend>${state.language === "it" ? "Lingua selezionata" : "Selected language"} · ${state.language === "it" ? "Italiano" : "English"}</legend>
      <label>${labels.prompt}<textarea name="${prefix}Prompt" rows="2" required></textarea></label>
      <label>${labels.correct}<input name="${prefix}Correct" required></label>
      <div class="form-row">
        <label>${labels.wrong} 1<input name="${prefix}Wrong1" required></label>
        <label>${labels.wrong} 2<input name="${prefix}Wrong2" required></label>
      </div>
      <label>${labels.wrong} 3<input name="${prefix}Wrong3" required></label>
      <label>${labels.hint}<textarea name="${prefix}Hint" rows="2" required></textarea></label>
      <label>${labels.explanation}<textarea name="${prefix}Explanation" rows="2" required></textarea></label>
    </fieldset>`;
}

function questionFromForm(values, prefix = "") {
  const correct = values.get(`${prefix}Correct`).trim();
  return {
    prompt: values.get(`${prefix}Prompt`).trim(),
    answers: [
      correct,
      values.get(`${prefix}Wrong1`).trim(),
      values.get(`${prefix}Wrong2`).trim(),
      values.get(`${prefix}Wrong3`).trim()
    ],
    correct_answer: correct,
    hint: values.get(`${prefix}Hint`).trim(),
    explanation: values.get(`${prefix}Explanation`).trim()
  };
}

export async function saveTeacherExercise(payload) {
  if (Object.prototype.hasOwnProperty.call(payload, "selected_learning_topic_code")) {
    const { data, error } = await supabase.rpc("save_teacher_learning_activity", payload);
    if (error) throw error;
    await loadAccount();
    return data;
  }
  let { data, error } = await supabase.rpc("save_teacher_exercise_level", payload);
  // During a staged database rollout the older RPC can still be active. Keep
  // authoring usable until the canonical-topic migration is applied.
  const missingNewSignature = error && (
    error.code === "PGRST202" ||
    /function|schema cache|selected_topic_code/i.test(error.message || "")
  );
  if (missingNewSignature && Object.prototype.hasOwnProperty.call(payload, "selected_topic_code")) {
    const legacyPayload = { ...payload };
    delete legacyPayload.selected_topic_code;
    const legacyResult = await supabase.rpc("save_teacher_exercise_level", legacyPayload);
    data = legacyResult.data;
    error = legacyResult.error;
  }
  if (error) throw error;
  await loadAccount();
  return data;
}

export function createManualExerciseModal() {
  const teacherClasses = state.schoolClasses.filter(item => item.teacher_name === state.account.username);
  const questions = [];
  let editingQuestion = null;
  let titleWasEdited = false;
  showModal(`
    <div class="manual-exercise-builder">
      <div class="modal-header">
        <div><span class="eyebrow">${state.language === "it" ? "Editor guidato" : "Guided editor"}</span><h2>${state.language === "it" ? "Crea un esercizio" : "Create an exercise"}</h2></div>
        <button class="close-button" data-close-modal>×</button>
      </div>
      <div class="manual-builder-steps" aria-label="${state.language === "it" ? "Passaggi creazione" : "Creation steps"}">
        <span class="active"><b>1</b>${state.language === "it" ? "Contesto" : "Context"}</span>
        <span><b>2</b>${state.language === "it" ? "Domande" : "Questions"}</span>
        <span><b>3</b>${state.language === "it" ? "Conferma" : "Confirm"}</span>
      </div>
      <form id="manual-exercise-form" class="manual-builder-form">
        <section class="manual-builder-section manual-context-section">
          <div class="manual-section-heading"><span>1</span><div><h3>${state.language === "it" ? "Dove verrà assegnato?" : "Where will it be assigned?"}</h3><p>${state.language === "it" ? "Scegli la classe: mostreremo solo gli argomenti compatibili." : "Choose the class: only compatible topics will be shown."}</p></div></div>
          <div class="manual-context-grid">
            <label>${state.language === "it" ? "Classe" : "Class"}<select name="classID" required>
              <option value="">${state.language === "it" ? "Seleziona una classe" : "Select a class"}</option>
              ${teacherClasses.map(classroom => `<option value="${classroom.class_id}">${escapeHTML(classroom.school_name)} · ${escapeHTML(classroom.class_name)} · ${escapeHTML(classroom.academic_year_name)}</option>`).join("")}
            </select></label>
            <label>${state.language === "it" ? "Argomento" : "Topic"}<select name="topicCode" required disabled>
              <option value="">${state.language === "it" ? "Prima scegli la classe" : "Choose the class first"}</option>
            </select></label>
            <label>${state.language === "it" ? "Difficoltà" : "Difficulty"}<select name="difficulty" required>
              <option value="1">🌱 ${state.language === "it" ? "Base" : "Basic"}</option>
              <option value="3">⭐ ${state.language === "it" ? "Medio" : "Intermediate"}</option>
              <option value="6">🚀 ${state.language === "it" ? "Avanzato" : "Advanced"}</option>
              <option value="9">🏆 ${state.language === "it" ? "Esperto" : "Expert"}</option>
            </select></label>
          </div>
          <label>${state.language === "it" ? "Titolo dell’esercizio" : "Exercise title"}<input name="title" placeholder="${state.language === "it" ? "Verrà proposto automaticamente" : "A title will be suggested automatically"}" required></label>
          <div class="manual-context-preview" id="manual-context-preview">${state.language === "it" ? "Seleziona classe e argomento per vedere il percorso." : "Select class and topic to preview the path."}</div>
        </section>

        <section class="manual-builder-section manual-question-section">
          <div class="manual-section-heading"><span>2</span><div><h3>${state.language === "it" ? "Aggiungi le domande" : "Add questions"}</h3><p>${state.language === "it" ? "Inserisci una domanda alla volta. Le raccoglieremo nello stesso esercizio." : "Add one question at a time. They will be collected in the same exercise."}</p></div></div>
          <div class="manual-question-editor">
            <label class="manual-prompt-field">${state.language === "it" ? "Testo della domanda" : "Question text"}<textarea name="draftPrompt" rows="3" placeholder="${state.language === "it" ? "Esempio: Quanto fa 18 + 7?" : "Example: What is 18 + 7?"}"></textarea></label>
            <div class="manual-answer-grid">
              <label class="manual-answer-field correct"><span>✓ ${state.language === "it" ? "Risposta corretta" : "Correct answer"}</span><input name="draftCorrect" placeholder="25"></label>
              <label class="manual-answer-field"><span>${state.language === "it" ? "Alternativa" : "Alternative"} 1</span><input name="draftWrong1" placeholder="24"></label>
              <label class="manual-answer-field"><span>${state.language === "it" ? "Alternativa" : "Alternative"} 2</span><input name="draftWrong2" placeholder="26"></label>
              <label class="manual-answer-field"><span>${state.language === "it" ? "Alternativa" : "Alternative"} 3</span><input name="draftWrong3" placeholder="27"></label>
            </div>
            <details class="manual-help-fields">
              <summary>＋ ${state.language === "it" ? "Aggiungi suggerimento e spiegazione (facoltativi)" : "Add hint and explanation (optional)"}</summary>
              <div class="form-grid">
                <label>${state.language === "it" ? "Suggerimento per il bambino" : "Hint for the learner"}<textarea name="draftHint" rows="2"></textarea></label>
                <label>${state.language === "it" ? "Spiegazione dopo la risposta" : "Explanation after answering"}<textarea name="draftExplanation" rows="2"></textarea></label>
              </div>
            </details>
            <div class="manual-question-actions">
              <button class="secondary" type="button" id="cancel-manual-question" hidden>${state.language === "it" ? "Annulla modifica" : "Cancel edit"}</button>
              <button class="primary" type="button" id="add-manual-question">＋ ${state.language === "it" ? "Aggiungi domanda" : "Add question"}</button>
            </div>
            <p class="manual-question-message" id="manual-question-message"></p>
          </div>
          <div class="manual-question-list" id="manual-question-list"></div>
        </section>

        <section class="manual-builder-section manual-confirm-section">
          <div class="manual-section-heading"><span>3</span><div><h3>${state.language === "it" ? "Controlla e assegna" : "Review and assign"}</h3><p>${state.language === "it" ? "L’esercizio verrà assegnato agli alunni della classe scelta." : "The exercise will be assigned to learners in the selected class."}</p></div></div>
          <div class="manual-final-summary" id="manual-final-summary"></div>
          <button class="primary full" type="submit" id="save-manual-exercise" disabled>${state.language === "it" ? "Salva e assegna alla classe" : "Save and assign to class"}</button>
          <p class="form-message"></p>
        </section>
      </form>
    </div>`);
  const form = document.querySelector("#manual-exercise-form");
  const classSelect = form.elements.classID;
  const topicSelect = form.elements.topicCode;
  const difficultySelect = form.elements.difficulty;
  const titleInput = form.elements.title;
  const addQuestionButton = document.querySelector("#add-manual-question");
  const cancelQuestionButton = document.querySelector("#cancel-manual-question");
  const saveButton = document.querySelector("#save-manual-exercise");
  const questionMessage = document.querySelector("#manual-question-message");

  const selectedTopic = () => getAIGenerationTopicOptions(classSelect.value)
    .find(option => option.topicCode === topicSelect.value);
  const suggestedTitle = () => {
    const topic = selectedTopic();
    return topic ? `${topic.topicName} · ${difficultyDisplay(Number(difficultySelect.value))}` : "";
  };
  const updateTitle = () => {
    if (!titleWasEdited || !titleInput.value.trim()) titleInput.value = suggestedTitle();
  };
  const updateSummary = () => {
    const classroom = teacherClasses.find(item => item.class_id === classSelect.value);
    const topic = selectedTopic();
    document.querySelector("#manual-context-preview").innerHTML = classroom && topic
      ? `<strong>${escapeHTML(classroom.school_name)} · ${escapeHTML(classroom.class_name)}</strong><span>${escapeHTML(topic.macroCategoryName)} › ${escapeHTML(topic.topicName)} › ${escapeHTML(difficultyDisplay(Number(difficultySelect.value)))}</span>`
      : (state.language === "it" ? "Seleziona classe e argomento per vedere il percorso." : "Select class and topic to preview the path.");
    document.querySelector("#manual-final-summary").innerHTML = `
      <div><strong>${questions.length}</strong><span>${questions.length === 1 ? t("question") : t("questions")}</span></div>
      <div><strong>${escapeHTML(topic ? topic.topicName : "—")}</strong><span>${state.language === "it" ? "argomento" : "topic"}</span></div>
      <div><strong>${escapeHTML(difficultyDisplay(Number(difficultySelect.value)))}</strong><span>${state.language === "it" ? "difficoltà" : "difficulty"}</span></div>`;
    saveButton.disabled = !(classroom && topic && titleInput.value.trim() && questions.length);
  };
  const renderTopicOptions = () => {
    const options = getAIGenerationTopicOptions(classSelect.value);
    const groups = new Map();
    options.forEach(option => {
      const key = option.macroCategoryName;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(option);
    });
    topicSelect.disabled = !options.length;
    topicSelect.innerHTML = `<option value="">${state.language === "it" ? "Seleziona un argomento" : "Select a topic"}</option>` +
      [...groups.entries()].map(([label, items]) => `<optgroup label="${escapeHTML(label)}">${items.map(option => `<option value="${escapeHTML(option.topicCode)}">${escapeHTML(option.topicName)}</option>`).join("")}</optgroup>`).join("");
    titleWasEdited = false;
    updateTitle();
    updateSummary();
  };
  const clearQuestionEditor = () => {
    ["draftPrompt", "draftCorrect", "draftWrong1", "draftWrong2", "draftWrong3", "draftHint", "draftExplanation"].forEach(name => {
      form.elements[name].value = "";
    });
    editingQuestion = null;
    addQuestionButton.textContent = `＋ ${state.language === "it" ? "Aggiungi domanda" : "Add question"}`;
    cancelQuestionButton.hidden = true;
    questionMessage.textContent = "";
  };
  const renderQuestions = () => {
    const container = document.querySelector("#manual-question-list");
    container.innerHTML = questions.length ? questions.map((question, index) => `
      <article class="manual-question-card">
        <span class="manual-question-number">${index + 1}</span>
        <div><strong>${escapeHTML(question.prompt)}</strong><small>✓ ${escapeHTML(question.correct_answer)}</small></div>
        <div class="manual-question-card-actions"><button type="button" data-edit-manual-question="${index}">✎</button><button type="button" data-delete-manual-question="${index}">×</button></div>
      </article>`).join("") : `<div class="manual-empty-questions"><span>✎</span><p>${state.language === "it" ? "Nessuna domanda ancora. Compila i campi qui sopra e premi “Aggiungi domanda”." : "No questions yet. Complete the fields above and select “Add question”."}</p></div>`;
    container.querySelectorAll("[data-edit-manual-question]").forEach(button => button.addEventListener("click", () => {
      editingQuestion = Number(button.dataset.editManualQuestion);
      const question = questions[editingQuestion];
      form.elements.draftPrompt.value = question.prompt;
      form.elements.draftCorrect.value = question.correct_answer;
      const wrongAnswers = question.answers.filter(answer => answer !== question.correct_answer);
      form.elements.draftWrong1.value = wrongAnswers[0] || "";
      form.elements.draftWrong2.value = wrongAnswers[1] || "";
      form.elements.draftWrong3.value = wrongAnswers[2] || "";
      form.elements.draftHint.value = question.hint;
      form.elements.draftExplanation.value = question.explanation;
      addQuestionButton.textContent = state.language === "it" ? "Salva modifica" : "Save changes";
      cancelQuestionButton.hidden = false;
      form.elements.draftPrompt.focus();
    }));
    container.querySelectorAll("[data-delete-manual-question]").forEach(button => button.addEventListener("click", () => {
      questions.splice(Number(button.dataset.deleteManualQuestion), 1);
      clearQuestionEditor();
      renderQuestions();
      updateSummary();
    }));
    updateSummary();
  };

  classSelect.addEventListener("change", renderTopicOptions);
  topicSelect.addEventListener("change", () => { titleWasEdited = false; updateTitle(); updateSummary(); });
  difficultySelect.addEventListener("change", () => { updateTitle(); updateSummary(); });
  titleInput.addEventListener("input", () => { titleWasEdited = true; updateSummary(); });
  cancelQuestionButton.addEventListener("click", clearQuestionEditor);
  addQuestionButton.addEventListener("click", () => {
    const prompt = form.elements.draftPrompt.value.trim();
    const correct = form.elements.draftCorrect.value.trim();
    const wrong = [form.elements.draftWrong1.value, form.elements.draftWrong2.value, form.elements.draftWrong3.value].map(value => value.trim());
    const answers = [correct, ...wrong];
    if (!prompt || answers.some(answer => !answer)) {
      questionMessage.textContent = state.language === "it" ? "Inserisci la domanda e tutte e quattro le risposte." : "Enter the question and all four answers.";
      return;
    }
    if (new Set(answers.map(answer => answer.toLocaleLowerCase())).size !== 4) {
      questionMessage.textContent = state.language === "it" ? "Le quattro risposte devono essere diverse." : "The four answers must be different.";
      return;
    }
    const question = {
      prompt,
      answers,
      correct_answer: correct,
      hint: form.elements.draftHint.value.trim() || (state.language === "it" ? "Rileggi la domanda ed elimina le risposte che non possono essere corrette." : "Read the question again and eliminate answers that cannot be correct."),
      explanation: form.elements.draftExplanation.value.trim() || (state.language === "it" ? `La risposta corretta è ${correct}.` : `The correct answer is ${correct}.`)
    };
    if (editingQuestion === null) questions.push(question);
    else questions[editingQuestion] = question;
    clearQuestionEditor();
    renderQuestions();
  });

  renderQuestions();
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const topic = selectedTopic();
    if (!topic || !questions.length) return updateSummary();
    setBusy(saveButton, true);
    try {
      const title = titleInput.value.trim();
      await saveTeacherExercise({
        selected_exercise: null,
        selected_class: classSelect.value,
        selected_learning_topic_code: topic.topicCode,
        selected_difficulty: Number(difficultySelect.value),
        selected_status: "Published",
        english_title: title,
        english_topic: topic.topicName,
        english_questions: questions,
        italian_title: title,
        italian_topic: topic.topicName,
        italian_questions: questions
      });
      closeModal();
      showToast(state.language === "it" ? `Esercizio creato con ${questions.length} domande e assegnato.` : `Exercise created with ${questions.length} questions and assigned.`);
      teacherExercisesView();
    } catch (error) {
      form.querySelector(".form-message").textContent = readableError(error, "Unable to save the exercise.");
      setBusy(saveButton, false);
    }
  });
}

async function appendQuestionModal(exerciseID) {
  const [{ data: english }, { data: italian }] = await Promise.all([
    supabase.rpc("get_teacher_exercise_detail", { selected_exercise: exerciseID, selected_language: "en" }),
    supabase.rpc("get_teacher_exercise_detail", { selected_exercise: exerciseID, selected_language: "it" })
  ]);
  if (!english?.[0] || !italian?.[0]) return showToast(state.language === "it" ? "Esercizio non disponibile." : "Exercise unavailable.");
  closeModal();
  showModal(`
    <div class="modal-header"><h2>${state.language === "it" ? "Aggiungi domanda" : "Add question"}</h2><button class="close-button" data-close-modal>×</button></div>
    <form id="append-question-form" class="form-grid">
      ${localizedQuestionFields("new")}
      <button class="primary full" type="submit">${state.language === "it" ? "Aggiungi e assegna" : "Add and assign"}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#append-question-form");
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const button = form.querySelector("button[type=submit]");
    const values = new FormData(form);
    setBusy(button, true);
    try {
      const question = questionFromForm(values, "new");
      await saveTeacherExercise({
        selected_exercise: exerciseID,
        selected_class: english[0].class_id,
        selected_difficulty: english[0].difficulty_level || 1,
        selected_status: "Published",
        english_title: english[0].title,
        english_topic: english[0].topic,
        english_questions: [...english[0].questions, question],
        italian_title: italian[0].title,
        italian_topic: italian[0].topic,
        italian_questions: [...italian[0].questions, question]
      });
      closeModal();
      showToast(state.language === "it" ? "Domanda aggiunta." : "Question added.");
      teacherExercisesView();
    } catch (error) {
      form.querySelector(".form-message").textContent = readableError(error, "Unable to add the question.");
      setBusy(button, false);
    }
  });
}
